import { AddOutlined, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { Button, ListSubheader, Menu, MenuItem } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { ReactElement, useMemo, useState } from 'react';
import { UserPermission, UserRole } from '@synergy/models';
import { hasPermission, hasRole } from '@synergy/utils';
import { useNavigate } from 'react-router';

import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { userSelector } from '../../../common/selectors/user-selectors';

interface CreateItem {
    label: string;
    to: string;
}

interface CreateGroup {
    header: string;
    items: CreateItem[];
}

export const PaloCreateNewMenu = (): ReactElement => {
    const navigate = useNavigate();
    const user = useAppSelector(userSelector);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const groups = useMemo((): CreateGroup[] => {
        const sales: CreateItem[] = [];
        if (hasPermission(UserPermission.SalesOrders, user)) {
            sales.push({ label: 'app.dashboard.new.sales.salesOrder', to: '/sales-orders/new' });
        }
        if (hasPermission(UserPermission.Demands, user)) {
            sales.push({ label: 'app.dashboard.new.sales.demand', to: '/demands/new' });
        }
        if (hasPermission(UserPermission.Invoices, user)) {
            sales.push({ label: 'app.dashboard.new.sales.invoice', to: '/invoices/new' });
        }

        const purchases: CreateItem[] = [];
        if (hasPermission([UserPermission.PurchaseOrders, UserPermission.Inquiries], user)) {
            purchases.push({ label: 'app.dashboard.new.purchases.inquiry', to: '/inquiries/new' });
        }
        if (hasPermission(UserPermission.PurchaseOrders, user)) {
            purchases.push({ label: 'app.dashboard.new.purchases.purchaseOrder', to: '/purchase-orders/new' });
        }

        const masterData: CreateItem[] = [];
        if (hasRole([UserRole.Admin, UserRole.SuperAdmin], user)) {
            masterData.push(
                { label: 'app.dashboard.new.product', to: ROUTES.products.product.new },
                { label: 'app.dashboard.new.contact.person', to: ROUTES.contactsAndOrganizations.contacts.new },
                { label: 'app.dashboard.new.contact.customer', to: ROUTES.contactsAndOrganizations.customers.new },
                { label: 'app.dashboard.new.contact.supplier', to: ROUTES.contactsAndOrganizations.suppliers.new }
            );
        }

        return [
            { header: 'app.dashboard.new.sales', items: sales },
            { header: 'app.dashboard.new.purchases', items: purchases },
            { header: 'app.navigation.masterData', items: masterData },
        ].filter(group => group.items.length > 0);
    }, [user]);

    const go = (to: string): void => {
        setAnchor(null);
        navigate(to);
    };

    return (
        <PaloThemeProvider>
            <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddOutlined />}
                endIcon={<KeyboardArrowDownOutlined />}
                onClick={event => setAnchor(event.currentTarget)}
            >
                <FormattedMessage id="app.dashboard.new" />
            </Button>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {groups.flatMap(group => [
                    <ListSubheader
                        key={group.header}
                        sx={{
                            lineHeight: '30px',
                            fontSize: '10.5px',
                            fontWeight: 700,
                            letterSpacing: '.06em',
                            textTransform: 'uppercase',
                        }}
                    >
                        <FormattedMessage id={group.header} />
                    </ListSubheader>,
                    ...group.items.map(item => (
                        <MenuItem key={item.to} onClick={() => go(item.to)}>
                            <FormattedMessage id={item.label} />
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </PaloThemeProvider>
    );
};
